<template>
  <section class="workbench-routes" :aria-label="$t('航线列表')">
    <div class="workbench-routes__heading"><h2 class="gs-section-heading"><span>{{ $t("航线") }}</span></h2><GroundStationStatusBadge tone="muted" class="workbench-routes__filter-badge"><UiCheckbox v-model="boundOnly" muted>{{ $t("已绑定") }}</UiCheckbox><span>{{ $t("{0} 条", [(routeItems.length)]) }}</span></GroundStationStatusBadge></div>
    <UiMotionList class="workbench-routes__list">
      <button v-for="item in routeItems" :key="item.route.id" type="button" class="workbench-route" :draggable="Boolean(item.route.route_data && active && canAssignRoute && mouseDragEnabled)" :class="{ 'is-selected': String(item.route.id) === selectedId }" :disabled="!item.route.route_data" :aria-pressed="String(item.route.id) === selectedId" @click="viewRoute(item.route)" @touchstart="startTouchDrag($event, item.route)" @contextmenu.prevent @dragstart="startRouteDrag($event, item.route)" @dragend="endRouteDrag($event)">
        <div class="workbench-route__header">
          <h3><span class="workbench-route__color" :style="{ backgroundColor: routeColors[String(item.route.id)] || item.route.color }" aria-hidden="true"></span><span class="workbench-route__name" :title="item.route.name || $t('航线 {0}', [item.route.id])">{{ item.route.name || $t('航线 {0}', [item.route.id]) }}</span></h3>
          <span class="workbench-route__id">{{ item.route.id }}</span>
        </div>
        <dl v-if="item.metrics"><div><dt>{{ $t("航点") }}</dt><dd>{{ $t("{0} 个", [(item.metrics.pointCount)]) }}</dd></div><div><dt>{{ $t("航程") }}</dt><dd>{{ distance(item.metrics.distanceMeters) }}</dd></div><div><dt>{{ $t("飞行时长") }}</dt><dd>{{ $t(duration(item.metrics.durationSeconds)) }}</dd></div></dl>
        <p v-else>{{ $t("暂缺有效航点") }}</p>
      </button>
    </UiMotionList>
    <UiEmpty v-if="!routeItems.length" :description="$t('暂无可查看航线')" />
  </section>
</template>

<script>
import { t } from '@/i18n'
import GroundStationStatusBadge from '@/components/ground-station/GroundStationStatusBadge'
import { calculateRouteMetrics, formatRouteDistance, formatFlightDuration } from '@/utils/routeMetrics'

export default {
  name: 'GroundStationRoutePanel',
  components: { GroundStationStatusBadge },
  props: { active: { type: Boolean, default: false }, canAssignRoute: { type: Boolean, default: false }, routes: { type: Array, default: () => [] }, boundRouteKeys: { type: Array, default: () => [] }, routeColors: { type: Object, default: () => ({}) } },
  data () { return { mouseDragEnabled: false, selectedId: '', boundOnly: true, touchDrag: null, suppressRouteClick: false } },
  computed: {
    routeItems () {
      const ids = new Set(this.boundRouteKeys.map(String))
      return this.routes.filter(route => !this.boundOnly || ids.has(`${route.shop_id}:${route.id}`)).map(route => ({ route, metrics: calculateRouteMetrics(route.route_data) }))
    }
  },
  watch: {
    active (active) {
      if (!active) { this.selectedId = ''; this.cancelTouchDrag() }
    },
    canAssignRoute (allowed) {
      if (!allowed) this.cancelTouchDrag()
    },
    routeItems (items) {
      if (!items.some(item => String(item.route.id) === this.selectedId)) this.selectedId = ''
    }
  },
  mounted () { this.mouseDragEnabled = window.matchMedia('(hover: hover) and (pointer: fine)').matches },
  beforeDestroy () { this.cancelTouchDrag() },
  methods: {
    routePreview (route) {
      return { id: route.id, name: route.name || `航线 ${route.id}`, shopId: route.shop_id }
    },
    startTouchDrag (event, route) {
      if (!this.active || !this.canAssignRoute || !route.route_data || event.touches.length !== 1) return
      this.cancelTouchDrag()
      const touch = event.touches[0]
      const drag = { id: touch.identifier, x: touch.clientX, y: touch.clientY, route: this.routePreview(route), source: event.currentTarget, active: false, target: null, ghost: null, timer: null }
      this.touchDrag = drag
      drag.timer = setTimeout(() => {
        if (this.touchDrag !== drag) return
        drag.active = true
        drag.source.classList.add('is-dragging')
        drag.ghost = drag.source.cloneNode(true)
        Object.assign(drag.ghost.style, { position: 'fixed', zIndex: '10000', width: `${drag.source.getBoundingClientRect().width}px`, pointerEvents: 'none', opacity: '.85', boxShadow: '0 12px 30px rgba(var(--ui-color-black-rgb), .25)' })
        document.body.appendChild(drag.ghost)
        this.moveTouchPreview(drag.x, drag.y)
      }, 350)
      document.addEventListener('touchmove', this.moveTouchDrag, { passive: false, capture: true })
      document.addEventListener('touchend', this.endTouchDrag, true)
      document.addEventListener('touchcancel', this.cancelTouchDrag)
    },
    findDragTouch (event) {
      return Array.from(event.changedTouches || []).find(touch => touch.identifier === this.touchDrag?.id)
    },
    moveTouchPreview (x, y) {
      const drag = this.touchDrag
      if (!drag?.active) return
      drag.ghost.style.left = `${x + 12}px`
      drag.ghost.style.top = `${y + 12}px`
      const target = this.findDropTarget(x, y)
      if (drag.target !== target) {
        drag.target?.classList.remove('is-route-drop-target')
        drag.target = target
        drag.target?.classList.add('is-route-drop-target')
      }
    },
    findDropTarget (x, y) {
      const direct = document.elementFromPoint(x, y)?.closest('[data-route-drop-target]')
      if (direct) return direct
      return Array.from(document.querySelectorAll('[data-route-drop-target]')).find(marker => {
        const rect = marker.getBoundingClientRect()
        return rect.width > 0 && rect.height > 0 && x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom
      }) || null
    },
    moveTouchDrag (event) {
      const drag = this.touchDrag
      if (!drag) return
      const touch = this.findDragTouch(event)
      if (!touch) return
      if (!drag.active) {
        if (Math.hypot(touch.clientX - drag.x, touch.clientY - drag.y) > 10) this.cancelTouchDrag()
        return
      }
      event.preventDefault()
      event.stopPropagation()
      this.moveTouchPreview(touch.clientX, touch.clientY)
    },
    endTouchDrag (event) {
      const drag = this.touchDrag
      const touch = drag && this.findDragTouch(event)
      if (!touch) return
      if (!this.canAssignRoute) { this.cancelTouchDrag(); return }
      if (drag.active) {
        event.preventDefault()
        event.stopPropagation()
        this.moveTouchPreview(touch.clientX, touch.clientY)
        drag.target?.dispatchEvent(new CustomEvent('flycube-route-touch-drop', { detail: drag.route }))
        this.suppressRouteClick = true
        setTimeout(() => { this.suppressRouteClick = false }, 400)
      }
      this.cancelTouchDrag()
    },
    cancelTouchDrag () {
      const drag = this.touchDrag
      if (drag) {
        clearTimeout(drag.timer)
        drag.source.classList.remove('is-dragging')
        drag.target?.classList.remove('is-route-drop-target')
        drag.ghost?.remove()
      }
      this.touchDrag = null
      document.removeEventListener('touchmove', this.moveTouchDrag, true)
      document.removeEventListener('touchend', this.endTouchDrag, true)
      document.removeEventListener('touchcancel', this.cancelTouchDrag)
    },
    startRouteDrag (event, route) {
      if (!this.mouseDragEnabled || !this.active || !this.canAssignRoute || !route.route_data) { event.preventDefault(); return }
      event.dataTransfer.effectAllowed = 'copy'
      event.dataTransfer.setData('application/x-flycube-route-preview', JSON.stringify(this.routePreview(route)))
      event.currentTarget.classList.add('is-dragging')
    },
    endRouteDrag (event) {
      event.currentTarget.classList.remove('is-dragging')
    },
    distance: formatRouteDistance,
    duration: formatFlightDuration,
    viewRoute (route) {
      if (!this.active || this.suppressRouteClick) return
      let data
      try {
        data = typeof route.route_data === 'string' ? JSON.parse(route.route_data) : route.route_data
      } catch (error) {
        if (!(error instanceof SyntaxError)) throw error
        this.$message.error(t(`航线 ${route.name || route.id} 数据格式错误，无法显示`))
        return
      }
      if (!Array.isArray(data?.questAss?.tasks) || !data.questAss.tasks.length) {
        this.$message.warning(t('此航线没有可显示的任务点'))
        return
      }
      this.selectedId = String(route.id)
      this.$emit('focus-route', data, String(route.id))
    },
    selectRoute (routeId) {
      if (!this.active) return
      const route = this.routeItems.find(item => String(item.route.id) === String(routeId))?.route
      if (!route) return
      this.viewRoute(route)
      this.$nextTick(() => {
        if (!this.active) return
        const selected = this.$el.querySelector('.workbench-route.is-selected')
        if (selected) selected.scrollIntoView({ block: 'nearest' })
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.workbench-routes { min-width: 0; min-height: 0; box-sizing: border-box; width: 100%; display: flex; flex-direction: column; padding: var(--ui-space-md) var(--ui-space-sm); gap: var(--ui-space-sm); }
.workbench-routes__heading { display: flex; align-items: center; justify-content: space-between; gap: var(--ui-space-sm); }
.workbench-routes__filter-badge { display: inline-flex; align-items: center; gap: var(--ui-space-xs); flex: none; white-space: nowrap; }
.workbench-routes__filter-badge .ui-checkbox { margin: 0; color: inherit; font-size: inherit; line-height: inherit; }
.workbench-routes__filter-badge > span { color: var(--ui-text-secondary); }
.workbench-routes__heading h2 { margin: 0; font-size: var(--gs-font-heading); }
.workbench-route p, .workbench-route dt { color: var(--ui-text-secondary); font-size: var(--ui-font-size-caption); }
.workbench-routes__list { min-width: 0; min-height: 0; overflow: auto; }
.workbench-route { display: block; box-sizing: border-box; width: 100%; text-align: left; font: inherit; color: inherit; cursor: pointer; padding: var(--ui-space-sm); margin-bottom: var(--ui-space-sm); border: 1px solid var(--ui-border-color); border-radius: var(--ui-radius-large); background: var(--ui-surface-finish), var(--ui-bg-elevated); }
.workbench-route.is-dragging { opacity: .55; transform: scale(.98); }
.workbench-route:not(:disabled) { transition: transform .18s ease, opacity .18s ease, border-color .18s ease; }
@media (prefers-reduced-motion: reduce) { .workbench-route:not(:disabled) { transition: none; } }
.workbench-route:focus-visible { outline: 2px solid var(--ui-action-primary); outline-offset: -2px; }
.workbench-route:disabled { cursor: not-allowed; opacity: .6; }
@media (hover: hover) and (pointer: fine) { .workbench-route:hover:not(:disabled) { border-color: var(--ui-border-color-strong); } }
.workbench-route.is-selected { border-color: var(--ui-action-primary); }
.workbench-route__header { display: flex; align-items: center; gap: var(--ui-space-sm); }
.workbench-route h3 { margin: 0; min-width: 0; flex: 1; display: flex; align-items: center; gap: var(--ui-space-xs); font-size: var(--ui-font-size-body); }
.workbench-route__name { min-width: 0; overflow: hidden; white-space: nowrap; text-overflow: ellipsis; }
.workbench-route__id { flex-shrink: 0; color: var(--ui-text-secondary); font-size: var(--ui-font-size-caption); font-variant-numeric: tabular-nums; }
.workbench-route__color { width: 6px; height: 6px; flex-shrink: 0; border-radius: 50%; background: var(--ui-color-info); }
.workbench-route dl { display: grid; gap: var(--ui-space-xs); margin: var(--ui-space-sm) 0 0; line-height: 20px; }
.workbench-route dl > div { display: flex; align-items: baseline; justify-content: space-between; gap: var(--ui-space-sm); }
.workbench-route dt { flex-shrink: 0; }
.workbench-route dd { margin: 0; text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums; }
@media (max-height: 540px) and (orientation: landscape) { .workbench-route { padding: 6px; } .workbench-route dl > div { gap: 2px; } .workbench-route dt { font-size: 11px; } }
</style>
